<template>
  <div id="listWidget" class="mb-5">
    <div v-if="registMode">
      <WizardZoneSelectStep
        v-if="wizardStep === 3"
        :step="wizardStep"
        :zones="zones"
        :selectedZone="selectedZone"
        :handleChange="onSelectZoneChange"
      />
      <WizardTransferCompleteStep
        v-if="wizardStep === 4"
        :step="wizardStep"
        :controller="connectedController"
      />
    </div>
    <div v-else>
      <WizardTransferCompleteStep
        v-if="wizardStep === 3"
        :step="wizardStep"
        :controller="connectedController"
      />
    </div>

    <!-- 진행 단계 표시 (이관 처리 단계부터 표시) -->
    <div class="mt-4 mb-4" v-if="wizardStep >= 2">
      <div class="step-container">
        <div
          v-for="(step, index) in registrationSteps"
          :key="index"
          class="step-item"
        >
          <div class="step-content">
            <div
              class="step-indicator"
              :class="{
                'step-pending': step.status === 'pending',
                'step-in-progress': step.status === 'in_progress',
                'step-completed': step.status === 'completed',
                'step-failed': step.status === 'failed',
              }"
            >
              <div class="step-number">
                <span v-if="step.status === 'completed'">✓</span>
                <span v-else-if="step.status === 'failed'">✗</span>
                <span v-else-if="step.status === 'in_progress'">
                  <span
                    class="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                </span>
                <span v-else>{{ index + 1 }}</span>
              </div>
            </div>
            <div class="step-label">
              <div class="fw-bold">{{ step.title }}</div>
              <div
                v-if="step.status === 'in_progress' && step.message"
                class="text-muted mt-1"
                style="font-size: 0.75rem"
              >
                {{ step.message }}
              </div>
              <div
                v-if="step.status === 'failed' && step.error"
                class="text-danger mt-1"
                style="font-size: 0.75rem"
              >
                {{ step.error }}
              </div>
            </div>
          </div>
          <div
            v-if="index < registrationSteps.length - 1"
            class="step-connector"
            :class="{
              'connector-active': step.status === 'completed',
            }"
          ></div>
        </div>
      </div>
    </div>

    <!-- 연결 버튼 (wizardStep 1) -->
    <div v-if="wizardStep === 1" class="action-button-container mb-3">
      <button
        type="button"
        class="btn btn-primary w-100"
        @click="connectionDialog.show"
      >
        <i class="fa-solid fa-link me-2"></i>
        조명제어기 연결
      </button>
    </div>

    <!-- 연결된 컨트롤러 정보 카드 (완료 단계가 아닐 때만 표시) -->
    <ControllerCard
      v-if="connectedController._id && wizardStep !== 3 && wizardStep !== 4"
      :controller="connectedController"
      controller-type="조명제어기"
      mode="wizard"
    />

    <!-- ControllerConnectionDialog -->
    <ControllerConnectionDialog
      :visible="connectionDialog.visible"
      title="조명제어기 연결 및 설정"
      actionType="transfer"
      :needsProvisioning="true"
      :needsWifiInput="true"
      :initialWifiSsid="settingParams.ssid"
      :initialWifiPassword="settingParams.pwd"
      @complete="onConnectionComplete"
      @cancel="connectionDialog.hide"
    />
  </div>

  <div id="footer" class="app-footer ms-0">
    <footer class="mt-auto">
      <div class="row">
        <div class="col" />
        <div class="col" />
        <div class="col">
          <button
            v-if="wizardStep === 3 && registMode"
            type="button"
            class="btn btn-primary btn-lg"
            style="width: 100%"
            @click="onNextStepClick"
          >
            등록
          </button>
          <button
            v-if="wizardStep === 3 && !registMode"
            type="button"
            class="btn btn-primary btn-lg"
            style="width: 100%"
            @click="onNextStepClick"
          >
            완료
          </button>
          <button
            v-if="wizardStep === 4"
            type="button"
            class="btn btn-primary btn-lg"
            style="width: 100%"
            @click="onNextStepClick"
          >
            완료
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, reactive, getCurrentInstance, computed, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import useMobile from '@/common/hooks/useMobile';
import WizardZoneSelectStep from './components/wizard/WizardZoneSelectStep';
import WizardTransferCompleteStep from './components/wizard/WizardTransferCompleteStep';
import ControllerCard from './components/ControllerCard.vue';
import ControllerConnectionDialog from './components/ControllerConnectionDialog.vue';
import useModal from '@/common/hooks/useModal';
import { SettingConfig } from '@/common/constants';

const store = useStore();
const router = useRouter();
const {
  getControllerInfo,
  requestTransferController,
  requestRegistController,
} = useMobile();
const { emitter } =
  getCurrentInstance().appContext.config.globalProperties;

// 팝업 상태 관리
const connectionDialog = reactive(useModal());

const settingParams = ref({
  ssid: '',
  pwd: '',
  firmware_version: '',
  firmware_url: '',
  uuid: '',
  device_key: '',
  mqtt_url: process.env.VUE_APP_CTRL_MQTT_DOMAIN,
  mqtt_username: '',
  mqtt_password: '',
});

const connectedController = ref({
  _id: '',
  version: '',
  model: '',
});
const wizardStep = ref(1);
const account = computed(() => store.getters['account']);
const zones = computed(() => store.getters['home/getZones']);
const selectedZone = ref(undefined);
const registMode = ref(false);
let requestCount = 0;

// 진행 상태 관리 (이관 처리 관련만)
const registrationSteps = ref([
  {
    title: '이관 처리',
    status: 'pending', // pending, in_progress, completed, failed
    message: '',
    error: '',
  },
  {
    title: '완료',
    status: 'pending',
    message: '',
    error: '',
  },
]);

const updateStepStatus = (stepIndex, status, message = '', error = '') => {
  if (stepIndex >= 0 && stepIndex < registrationSteps.value.length) {
    registrationSteps.value[stepIndex].status = status;
    registrationSteps.value[stepIndex].message = message;
    registrationSteps.value[stepIndex].error = error;
  }
};

onBeforeUnmount(() => {
  // 팝업에서 자동으로 정리되므로 별도 처리 불필요
});

const showPopAlert = (msg) => {
  emitter.emit('showPopAlert', {
    description: msg,
  });
};

const onSelectZoneChange = (e) => {
  const zone = zones.value.find((zone) => zone._id == e.target.value);
  selectedZone.value = zone;
};

// 연결 완료 핸들러
const onConnectionComplete = async (result) => {
  const { controller, connectionType, wifiSsid, wifiPassword } = result;
  
  // 연결된 컨트롤러 정보 저장
  connectedController.value = controller;
  
  // WiFi 설정 정보 저장
  settingParams.value.ssid = wifiSsid;
  settingParams.value.pwd = wifiPassword;
  
  // 팝업 닫기
  connectionDialog.hide();
  
  // 이관 처리 시작
  await processTransfer();
};

// 이관 처리 로직
const processTransfer = async () => {
  updateStepStatus(0, 'in_progress', '이관 처리 확인 중...');
  requestCount = 0;
  emitter.emit('showLoading');
  
  const interval = setInterval(async () => {
    requestCount++;
    
    emitter.emit('setLoadingText', requestCount == 0 ? '조명제어기의 등록 여부를 확인하고 있습니다.' : `조명제어기의 등록 여부 확인을 ${requestCount + 1}번째 시도하고 있습니다.`);
    updateStepStatus(0, 'in_progress', `등록 여부 확인 중... (${requestCount + 1}번째 시도)`);
    const result = await getControllerInfo(connectedController.value);
    if (result.resultCode) {
      if (result.resultData) {
        // 이미 등록된 컨트롤러일때 이관
        emitter.emit('setLoadingText', '조명제어기를 이관받고 있습니다.');
        updateStepStatus(0, 'in_progress', '이관받는 중...');
        const transResult = await requestTransferController(
          connectedController.value,
          account.value._id,
        );

        if (transResult?.resultCode === "0000") {
          await store.dispatch('home/fetchAllZones');
          updateStepStatus(0, 'completed', '이관 완료');
          updateStepStatus(1, 'completed', '완료');
          clearInterval(interval);
          wizardStep.value = 3; // 완료 단계로 이동
          emitter.emit('hideLoading');
          return;
        } else {
          updateStepStatus(0, 'failed', '', transResult.resultMessage || '이관 실패');
          showPopAlert(transResult.resultMessage);
          clearInterval(interval);
          emitter.emit('hideLoading');
          return;
        }
      } else {
        // 등록되지 않은 컨트롤러일때 등록모드
        registMode.value = true;
        updateStepStatus(0, 'completed', '등록 필요');
      }

      clearInterval(interval);
      wizardStep.value = 3; // 영역 선택 단계로 이동
      emitter.emit('hideLoading');
      return;
    }

    if (requestCount >= SettingConfig.requestCount) {
      clearInterval(interval);
      updateStepStatus(0, 'failed', '', '컨트롤러 이관 실패');
      emitter.emit('hideLoading');
      showPopAlert('컨트롤러 이관에 실패했습니다.');
    }
  }, SettingConfig.requestInterval);
};

const onNextStepClick = async () => {
  try {
    if (registMode.value) {
      // 등록모드일때
      if (wizardStep.value === 3) {
        if (!selectedZone.value) {
          showPopAlert('조명제어기를 등록할 영역을 선택해주새요.');
          return;
        }

        updateStepStatus(0, 'in_progress', '서버 등록 중...');
        const request = () => {
          return new Promise(async (resolve, reject) => {
            setTimeout(async () => {
              try {
                const result = await requestRegistController(
                  connectedController.value,
                  selectedZone.value._id,
                );
                resolve(result);
              } catch (e) {
                reject(e);
              }
            }, SettingConfig.requestInterval);
          });
        };

        emitter.emit('showLoading');
        let result = undefined;
        for (let i = 0; i < SettingConfig.requestCount; i++) {
          emitter.emit('setLoadingText', i == 0 ? '조명제어기를 등록하고 있습니다.' : `조명제어기 등록을 ${i + 1}번째 시도하고 있습니다.`);
          updateStepStatus(0, 'in_progress', `서버 등록 중... (${i + 1}번째 시도)`);
          result = await request();
          if (result?.resultCode === "0000") {
            await store.dispatch('home/fetchAllZones');
            updateStepStatus(0, 'completed', '서버 등록 완료');
            updateStepStatus(1, 'completed', '완료');

            wizardStep.value += 1;
            emitter.emit('hideLoading');
            return;
          }
          if (result?.resultCode === "1001") {
            break;
          }
        }

        updateStepStatus(0, 'failed', '', result?.resultMessage || '서버 등록 실패');
        emitter.emit('hideLoading');
        showPopAlert(result?.resultMessage?.length > 0 ? result.resultMessage : "컨트롤러 등록에 실패했습니다.");
        return;
      } else if (wizardStep.value === 4) {
        emitter.emit('showPopAlert', {
          description: "이관받기가 완료되었습니다.",
          okCallback: router.go(-1),
          cancelCallback: router.go(-1)
        });          
        return;
      }
    } else {
      // 이관모드일때
      if (wizardStep.value === 3) {
        emitter.emit('showPopAlert', {
          description: "이관받기가 완료되었습니다.",
          okCallback: router.go(-1),
          cancelCallback: router.go(-1)
        });          
        return;
      }
    }
  } catch (err) {
    if (err?.length > 0) {
      showPopAlert(err);
    }
  }
};
</script>

<style scoped>
.step-container {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.step-item {
  position: relative;
  display: flex;
  flex-direction: column;
}

.step-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.5rem 0;
}

.step-indicator {
  width: 50px;
  height: 50px;
  min-width: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.step-pending {
  background-color: #e9ecef;
  color: #6c757d;
  border: 2px solid #dee2e6;
}

.step-in-progress {
  background-color: #0d6efd;
  color: white;
  border: 2px solid #0d6efd;
  animation: pulse 1.5s ease-in-out infinite;
}

.step-completed {
  background-color: #198754;
  color: white;
  border: 2px solid #198754;
}

.step-failed {
  background-color: #dc3545;
  color: white;
  border: 2px solid #dc3545;
}

.step-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.step-label {
  flex: 1;
  padding-top: 0.5rem;
}

.step-connector {
  position: absolute;
  left: 25px;
  top: 75px;
  width: 2px;
  height: calc(100% - 50px);
  background-color: #dee2e6;
  z-index: 0;
}

.connector-active {
  background-color: #198754;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

.action-button-container {
  margin-top: 0;
}

.action-button-container .btn {
  padding: 0.625rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.action-button-container .btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}
</style>
